import { useRouteError, isRouteErrorResponse, Link, useLocation } from 'react-router-dom';

interface ErrorDetails {
  status: number;
  title: string;
  message: string;
  emoji: string;
}

function getErrorDetails(error: unknown): ErrorDetails {
  if (isRouteErrorResponse(error)) {
    switch (error.status) {
      case 404:
        return {
          status: 404,
          title: 'Page Not Found',
          message: "The page you're looking for doesn't exist or has been moved.",
          emoji: '🔍',
        };
      case 401:
        return {
          status: 401,
          title: 'Unauthorized',
          message: 'You need to be logged in to access this page.',
          emoji: '🔐',
        };
      case 403:
        return {
          status: 403,
          title: 'Forbidden',
          message: "You don't have permission to access this resource.",
          emoji: '🚫',
        };
      case 500:
        return {
          status: 500,
          title: 'Server Error',
          message: 'Something went wrong on our end. Please try again later.',
          emoji: '🔥',
        };
      case 503:
        return {
          status: 503,
          title: 'Service Unavailable',
          message: 'The server is temporarily unavailable. Please try again later.',
          emoji: '🔧',
        };
      default:
        return {
          status: error.status,
          title: error.statusText || 'Error',
          message: error.data?.message || 'An unexpected error occurred.',
          emoji: '⚠️',
        };
    }
  }

  // Handle non-route errors (JavaScript errors, etc.)
  if (error instanceof Error) {
    return {
      status: 500,
      title: 'Application Error',
      message: error.message || 'An unexpected error occurred.',
      emoji: '💥',
    };
  }

  return {
    status: 500,
    title: 'Unknown Error',
    message: 'An unexpected error occurred.',
    emoji: '❓',
  };
}

/**
 * Error page component for route-level errors
 */
export function ErrorPage() {
  const error = useRouteError();
  const details = getErrorDetails(error);

  return (
    <div className="min-h-screen bg-surface-0 flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <div className="text-8xl mb-6">{details.emoji}</div>
        <h1 className="text-4xl font-bold font-mono text-text-primary mb-2">{details.status}</h1>
        <h2 className="text-xl font-semibold text-text-secondary mb-4">{details.title}</h2>
        <p className="text-text-muted mb-8">{details.message}</p>
        <div className="flex gap-4 justify-center">
          <Link to="/" className="btn btn-primary">
            Go Home
          </Link>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-secondary"
          >
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Inline error component for use within pages
 */
export function InlineError({
  error,
  onRetry
}: {
  error: Error | string;
  onRetry?: () => void;
}) {
  const message = typeof error === 'string' ? error : error.message;

  return (
    <div className="bg-loss/10 border border-loss/30 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <span className="text-2xl">⚠️</span>
        <div className="flex-1">
          <h3 className="text-loss font-medium mb-1">Error</h3>
          <p className="text-loss/80 text-sm">{message}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="mt-3 text-sm text-loss hover:text-loss/80 underline"
            >
              Try again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Dedicated 404 page for catch-all routes
 */
export function NotFoundPage() {
  const location = useLocation();

  return (
    <div className="min-h-screen bg-surface-0 flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <div className="text-8xl mb-6">🔍</div>
        <h1 className="text-4xl font-bold font-mono text-text-primary mb-2">404</h1>
        <h2 className="text-xl font-semibold text-text-secondary mb-4">Page Not Found</h2>
        <p className="text-text-muted mb-2">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <p className="text-text-muted text-sm mb-8">
          Path: <code className="bg-surface-3 px-2 py-1 rounded font-mono">{location.pathname}</code>
        </p>
        <div className="flex gap-4 justify-center">
          <Link to="/" className="btn btn-primary">
            Go Home
          </Link>
          <button
            onClick={() => window.history.back()}
            className="btn btn-secondary"
          >
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
}
